import { Article } from '../types';

export const detailedArticles: Article[] = [
  // ============ GETTING STARTED ============
  {
    id: 'start-1',
    title: 'Getting Started with Your CRM',
    slug: 'getting-started-guide',
    description: 'Complete guide to setting up and using your Defined Home Services CRM',
    category: 'getting-started',
    tags: ['setup', 'onboarding', 'basics', 'tutorial'],
    lastUpdated: new Date('2024-01-15'),
    readTime: 10,
    content: `
      <h2>Welcome to Defined Home Services CRM</h2>
      <p>This comprehensive guide will help you get started with your CRM system and make the most of its powerful features.</p>
      
      <h3 id="first-login">Your First Login</h3>
      <p>When you first access the CRM:</p>
      <ol>
        <li><strong>Navigate to the CRM URL</strong> - Open your browser and go to your CRM login page</li>
        <li><strong>Enter your credentials</strong> - Use the email and password provided by your administrator</li>
        <li><strong>Two-factor authentication</strong> - If enabled, enter the code sent to your phone or email</li>
        <li><strong>Accept terms</strong> - Review and accept the terms of service on first login</li>
      </ol>
      
      <h3 id="dashboard-overview">Understanding the Dashboard</h3>
      <p>Your dashboard is the command center of your CRM:</p>
      <ul>
        <li><strong>Navigation Menu</strong> - Located on the left side, provides access to all features</li>
        <li><strong>Quick Stats</strong> - View today's appointments, pending tasks, and recent activities</li>
        <li><strong>Notifications</strong> - Important alerts and reminders appear at the top</li>
        <li><strong>Quick Actions</strong> - Buttons for common tasks like creating appointments or invoices</li>
      </ul>
      
      <h3 id="company-setup">Setting Up Your Company Profile</h3>
      <p>Complete your company information for professional documents:</p>
      <ol>
        <li>Go to Settings → Company Profile</li>
        <li>Upload your company logo (recommended: 300x100px PNG)</li>
        <li>Enter business details:
          <ul>
            <li>Company name and DBA</li>
            <li>Business address</li>
            <li>Phone and email</li>
            <li>Tax ID / Business license number</li>
            <li>Website and social media links</li>
          </ul>
        </li>
        <li>Configure business hours and service areas</li>
        <li>Set up payment terms and policies</li>
      </ol>
      
      <h3 id="user-profile">Customizing Your User Profile</h3>
      <p>Personalize your account:</p>
      <ul>
        <li><strong>Profile Picture</strong> - Upload a professional photo for customer communications</li>
        <li><strong>Contact Info</strong> - Keep your phone and email current</li>
        <li><strong>Signature</strong> - Create an email signature for automated messages</li>
        <li><strong>Notifications</strong> - Choose which alerts you want to receive</li>
        <li><strong>Calendar Sync</strong> - Connect Google or Outlook calendar</li>
      </ul>
      
      <h3 id="navigation-tips">Navigation Tips</h3>
      <div class="bg-blue-50 p-4 rounded-lg">
        <p><strong>Pro Tips for Efficient Navigation:</strong></p>
        <ul>
          <li>Use keyboard shortcuts: Press '?' to see all available shortcuts</li>
          <li>Pin frequently used pages to your browser bookmarks</li>
          <li>Use the global search (Ctrl+K) to quickly find customers or appointments</li>
          <li>Customize your dashboard widgets for your workflow</li>
        </ul>
      </div>
      
      <h3 id="mobile-access">Accessing CRM on Mobile</h3>
      <p>Stay productive on the go:</p>
      <ul>
        <li>The CRM is fully responsive and works on all devices</li>
        <li>Save the URL to your home screen for app-like access</li>
        <li>Use mobile-optimized views for field work</li>
        <li>Access maps and directions directly from appointments</li>
      </ul>
      
      <h3 id="getting-help">Getting Help</h3>
      <p>Multiple support resources are available:</p>
      <ul>
        <li><strong>This Help Center</strong> - Comprehensive guides for all features</li>
        <li><strong>In-app tooltips</strong> - Hover over '?' icons for quick help</li>
        <li><strong>Support Chat</strong> - Live chat during business hours</li>
        <li><strong>Email Support</strong> - support@definedcleaning.com</li>
        <li><strong>Video Tutorials</strong> - Available in the Training section</li>
      </ul>
    `,
    tableOfContents: [
      { id: 'first-login', title: 'Your First Login' },
      { id: 'dashboard-overview', title: 'Understanding the Dashboard' },
      { id: 'company-setup', title: 'Setting Up Your Company Profile' },
      { id: 'user-profile', title: 'Customizing Your User Profile' },
      { id: 'navigation-tips', title: 'Navigation Tips' },
      { id: 'mobile-access', title: 'Accessing CRM on Mobile' },
      { id: 'getting-help', title: 'Getting Help' }
    ],
    relatedArticles: ['adding-new-customers', 'creating-appointments', 'creating-invoices']
  },

  // ============ CUSTOMER MANAGEMENT ============
  {
    id: 'cust-1',
    title: 'Complete Guide to Customer Management',
    slug: 'customer-management-guide',
    description: 'Master customer data management, from adding new clients to maintaining relationships',
    category: 'customers',
    tags: ['customers', 'contacts', 'database', 'management'],
    lastUpdated: new Date('2024-01-20'),
    readTime: 12,
    content: `
      <h2>Customer Management in Your CRM</h2>
      <p>Effective customer management is the foundation of your business success. This guide covers everything you need to know about managing your customer database.</p>
      
      <h3 id="adding-customers">Adding New Customers</h3>
      <p>There are multiple ways to add customers to your CRM:</p>
      
      <h4>Manual Entry</h4>
      <ol>
        <li>Click "Customers" in the sidebar menu</li>
        <li>Click the "New Customer" button (top right)</li>
        <li>Fill in the customer information form:
          <ul>
            <li><strong>Basic Information</strong>
              <ul>
                <li>First and Last Name (required)</li>
                <li>Email address</li>
                <li>Phone numbers (mobile/home/work)</li>
                <li>Preferred contact method</li>
              </ul>
            </li>
            <li><strong>Address Information</strong>
              <ul>
                <li>Service address (required for appointments)</li>
                <li>Billing address (if different)</li>
                <li>Property type (residential/commercial)</li>
                <li>Access instructions or gate codes</li>
              </ul>
            </li>
            <li><strong>Additional Details</strong>
              <ul>
                <li>Customer source (referral, website, etc.)</li>
                <li>Service preferences</li>
                <li>Special notes or requirements</li>
                <li>Pricing tier or discounts</li>
              </ul>
            </li>
          </ul>
        </li>
        <li>Click "Save Customer"</li>
      </ol>
      
      <h4>Quick Add During Appointment Booking</h4>
      <p>When scheduling an appointment for a new customer:</p>
      <ol>
        <li>In the appointment form, select "New Customer"</li>
        <li>Enter minimal required info (name, phone, address)</li>
        <li>The system creates the customer profile automatically</li>
        <li>Complete the full profile later as needed</li>
      </ol>
      
      <h3 id="importing-customers">Bulk Import via CSV</h3>
      <p>Import multiple customers at once from spreadsheets:</p>
      <ol>
        <li>Prepare your CSV file with these columns:
          <ul>
            <li>FirstName, LastName (required)</li>
            <li>Email, Phone</li>
            <li>Address, City, State, ZIP</li>
            <li>Notes, Source, Tags</li>
          </ul>
        </li>
        <li>Go to Customers → Import</li>
        <li>Upload your CSV file</li>
        <li>Map columns to CRM fields</li>
        <li>Review and fix any validation errors</li>
        <li>Confirm import</li>
      </ol>
      
      <div class="bg-yellow-50 p-4 rounded-lg mt-4">
        <strong>Import Tips:</strong>
        <ul>
          <li>Download the sample CSV template first</li>
          <li>Clean your data before importing (remove duplicates, fix formatting)</li>
          <li>Test with a small batch first</li>
          <li>The system will detect and flag potential duplicates</li>
        </ul>
      </div>
      
      <h3 id="customer-profiles">Managing Customer Profiles</h3>
      <p>Each customer profile contains multiple sections:</p>
      
      <h4>Contact Information Tab</h4>
      <ul>
        <li>All phone numbers and emails</li>
        <li>Preferred contact method and best times to call</li>
        <li>Emergency contacts</li>
        <li>Communication preferences (email/SMS opt-in)</li>
      </ul>
      
      <h4>Service History Tab</h4>
      <ul>
        <li>All past appointments and services</li>
        <li>Service frequency and patterns</li>
        <li>Favorite services and technicians</li>
        <li>Special instructions for service</li>
      </ul>
      
      <h4>Billing Tab</h4>
      <ul>
        <li>Payment methods on file</li>
        <li>Invoice history and outstanding balances</li>
        <li>Pricing agreements or contracts</li>
        <li>Tax exemption status</li>
      </ul>
      
      <h4>Notes & Documents Tab</h4>
      <ul>
        <li>Internal notes (not visible to customer)</li>
        <li>Service agreements and contracts</li>
        <li>Photos of property or completed work</li>
        <li>Insurance documents</li>
      </ul>
      
      <h3 id="searching-filtering">Searching and Filtering Customers</h3>
      <p>Find customers quickly using powerful search tools:</p>
      <ul>
        <li><strong>Quick Search</strong> - Type name, phone, or address in the search bar</li>
        <li><strong>Advanced Filters</strong>:
          <ul>
            <li>Service area/ZIP code</li>
            <li>Last service date range</li>
            <li>Customer tags or categories</li>
            <li>Outstanding balance</li>
            <li>Service frequency</li>
          </ul>
        </li>
        <li><strong>Saved Searches</strong> - Save frequently used filter combinations</li>
      </ul>
      
      <h3 id="customer-tags">Using Customer Tags and Categories</h3>
      <p>Organize customers with tags for better management:</p>
      <ul>
        <li><strong>Service Type Tags</strong>: "Window Cleaning", "Pressure Washing", "Solar Screens"</li>
        <li><strong>Frequency Tags</strong>: "Weekly", "Monthly", "Quarterly", "One-Time"</li>
        <li><strong>Priority Tags</strong>: "VIP", "Regular", "New Customer"</li>
        <li><strong>Location Tags</strong>: Neighborhoods or zones for route planning</li>
        <li><strong>Custom Tags</strong>: Create any tags specific to your business</li>
      </ul>
      
      <h3 id="customer-communication">Customer Communication Tools</h3>
      <p>Stay connected with built-in communication features:</p>
      <ul>
        <li><strong>Email Templates</strong> - Send appointment confirmations, reminders, and follow-ups</li>
        <li><strong>SMS Messaging</strong> - Text updates and quick communications</li>
        <li><strong>Call Logging</strong> - Record notes from phone conversations</li>
        <li><strong>Communication History</strong> - View all interactions in one timeline</li>
      </ul>
      
      <h3 id="duplicate-management">Managing Duplicate Customers</h3>
      <p>Keep your database clean:</p>
      <ol>
        <li>The system automatically detects potential duplicates</li>
        <li>Review suggested matches based on name, phone, or address</li>
        <li>Merge duplicates while preserving all history</li>
        <li>Set up duplicate prevention rules</li>
      </ol>
      
      <h3 id="customer-retention">Customer Retention Features</h3>
      <p>Tools to help maintain customer relationships:</p>
      <ul>
        <li><strong>Last Service Alerts</strong> - Identify customers who haven't booked recently</li>
        <li><strong>Birthday Reminders</strong> - Send special offers on birthdays</li>
        <li><strong>Service Due Notifications</strong> - Remind customers when regular service is due</li>
        <li><strong>Win-Back Campaigns</strong> - Re-engage inactive customers</li>
      </ul>
      
      <h3 id="data-security">Customer Data Security</h3>
      <div class="bg-green-50 p-4 rounded-lg">
        <strong>Security Best Practices:</strong>
        <ul>
          <li>All customer data is encrypted at rest and in transit</li>
          <li>Role-based access controls limit who can view/edit data</li>
          <li>Activity logs track all changes to customer records</li>
          <li>Regular backups ensure data recovery</li>
          <li>GDPR and privacy compliance features included</li>
        </ul>
      </div>
    `,
    tableOfContents: [
      { id: 'adding-customers', title: 'Adding New Customers' },
      { id: 'importing-customers', title: 'Bulk Import via CSV' },
      { id: 'customer-profiles', title: 'Managing Customer Profiles' },
      { id: 'searching-filtering', title: 'Searching and Filtering' },
      { id: 'customer-tags', title: 'Using Tags and Categories' },
      { id: 'customer-communication', title: 'Communication Tools' },
      { id: 'duplicate-management', title: 'Managing Duplicates' },
      { id: 'customer-retention', title: 'Retention Features' },
      { id: 'data-security', title: 'Data Security' }
    ],
    relatedArticles: ['importing-customers-csv', 'customer-communication', 'marketing-campaigns']
  },

  // ============ APPOINTMENTS ============
  {
    id: 'apt-1',
    title: 'Mastering Appointment Scheduling',
    slug: 'appointment-scheduling-guide',
    description: 'Complete guide to scheduling, managing, and optimizing your appointment calendar',
    category: 'appointments',
    tags: ['appointments', 'scheduling', 'calendar', 'booking'],
    lastUpdated: new Date('2024-01-22'),
    readTime: 15,
    content: `
      <h2>Appointment Scheduling System</h2>
      <p>Efficiently manage your service appointments with our comprehensive scheduling system. This guide covers all aspects of appointment management.</p>
      
      <h3 id="creating-appointments">Creating New Appointments</h3>
      <p>There are several ways to create appointments:</p>
      
      <h4>Method 1: Calendar View</h4>
      <ol>
        <li>Go to Appointments → Calendar</li>
        <li>Click on the desired date and time slot</li>
        <li>Fill in the appointment details:
          <ul>
            <li><strong>Customer</strong> - Select existing or create new</li>
            <li><strong>Service Type</strong> - Choose from your service menu</li>
            <li><strong>Date & Time</strong> - Adjust if needed</li>
            <li><strong>Duration</strong> - Estimated service time</li>
            <li><strong>Assigned To</strong> - Select technician or team</li>
            <li><strong>Status</strong> - Scheduled, Confirmed, or Tentative</li>
          </ul>
        </li>
        <li>Add any special instructions or notes</li>
        <li>Click "Save Appointment"</li>
      </ol>
      
      <h4>Method 2: Quick Book</h4>
      <ol>
        <li>Use the Quick Book button (available from any screen)</li>
        <li>Enter customer name or phone</li>
        <li>Select service and preferred date</li>
        <li>System suggests available time slots</li>
        <li>Confirm booking</li>
      </ol>
      
      <h4>Method 3: From Customer Profile</h4>
      <ol>
        <li>Open the customer's profile</li>
        <li>Click "Schedule Appointment"</li>
        <li>Customer info is pre-filled</li>
        <li>Select service and time</li>
        <li>Save appointment</li>
      </ol>
      
      <h3 id="calendar-views">Calendar Views and Navigation</h3>
      <p>Choose the view that works best for your workflow:</p>
      
      <h4>Day View</h4>
      <ul>
        <li>Hourly breakdown of appointments</li>
        <li>See conflicts and overlaps clearly</li>
        <li>Drag and drop to reschedule</li>
        <li>Color-coded by service type or technician</li>
      </ul>
      
      <h4>Week View</h4>
      <ul>
        <li>7-day overview with all appointments</li>
        <li>Perfect for planning routes</li>
        <li>Identify busy and slow periods</li>
        <li>Bulk actions for multiple appointments</li>
      </ul>
      
      <h4>Month View</h4>
      <ul>
        <li>High-level monthly overview</li>
        <li>See appointment counts per day</li>
        <li>Identify patterns and trends</li>
        <li>Long-term planning tool</li>
      </ul>
      
      <h4>List View</h4>
      <ul>
        <li>Table format with sortable columns</li>
        <li>Best for bulk updates</li>
        <li>Export to CSV for reporting</li>
        <li>Advanced filtering options</li>
      </ul>
      
      <h4>Map View</h4>
      <ul>
        <li>Geographic visualization of appointments</li>
        <li>Optimize routes between stops</li>
        <li>Identify service clusters</li>
        <li>Calculate drive times</li>
      </ul>
      
      <h3 id="appointment-details">Appointment Information</h3>
      <p>Each appointment contains comprehensive details:</p>
      
      <h4>Basic Information</h4>
      <ul>
        <li>Appointment ID and reference number</li>
        <li>Customer name and contact</li>
        <li>Service address with map link</li>
        <li>Service type and description</li>
        <li>Scheduled date and time</li>
        <li>Estimated duration</li>
      </ul>
      
      <h4>Service Details</h4>
      <ul>
        <li>Specific services to be performed</li>
        <li>Equipment or supplies needed</li>
        <li>Special instructions or access codes</li>
        <li>Photos from previous visits</li>
        <li>Pricing and estimates</li>
      </ul>
      
      <h4>Assignment Information</h4>
      <ul>
        <li>Primary technician assigned</li>
        <li>Helper or team members</li>
        <li>Vehicle or equipment assigned</li>
        <li>Route position for the day</li>
      </ul>
      
      <h3 id="recurring-appointments">Setting Up Recurring Appointments</h3>
      <p>Automate scheduling for regular customers:</p>
      <ol>
        <li>Create the first appointment</li>
        <li>Click "Make Recurring"</li>
        <li>Choose frequency:
          <ul>
            <li>Daily (specify days of week)</li>
            <li>Weekly (every X weeks)</li>
            <li>Monthly (by date or day of month)</li>
            <li>Quarterly</li>
            <li>Custom interval</li>
          </ul>
        </li>
        <li>Set end date or number of occurrences</li>
        <li>Review generated appointments</li>
        <li>Confirm creation</li>
      </ol>
      
      <div class="bg-blue-50 p-4 rounded-lg">
        <strong>Recurring Appointment Tips:</strong>
        <ul>
          <li>Set up seasonal adjustments (skip winter months, etc.)</li>
          <li>Link recurring appointments to service contracts</li>
          <li>Automatic price adjustments for long-term customers</li>
          <li>Bulk edit entire series when needed</li>
        </ul>
      </div>
      
      <h3 id="appointment-status">Appointment Status Management</h3>
      <p>Track appointment progress through stages:</p>
      <ul>
        <li><strong>Scheduled</strong> - Initial booking</li>
        <li><strong>Confirmed</strong> - Customer has confirmed</li>
        <li><strong>En Route</strong> - Technician is on the way</li>
        <li><strong>In Progress</strong> - Service being performed</li>
        <li><strong>Completed</strong> - Work finished</li>
        <li><strong>Cancelled</strong> - Appointment cancelled</li>
        <li><strong>No Show</strong> - Customer not available</li>
        <li><strong>Rescheduled</strong> - Moved to new date/time</li>
      </ul>
      
      <h3 id="conflict-prevention">Double Booking Prevention</h3>
      <p>The system helps prevent scheduling conflicts:</p>
      <ul>
        <li><strong>Real-time availability check</strong> - See technician schedules instantly</li>
        <li><strong>Conflict warnings</strong> - Alerts for overlapping appointments</li>
        <li><strong>Buffer time settings</strong> - Add travel time between appointments</li>
        <li><strong>Capacity limits</strong> - Set max appointments per day/technician</li>
        <li><strong>Override options</strong> - Managers can force bookings if needed</li>
      </ul>
      
      <h3 id="appointment-reminders">Automated Reminders</h3>
      <p>Reduce no-shows with automatic reminders:</p>
      <ol>
        <li>Configure reminder settings in Admin → Notifications</li>
        <li>Set timing (24 hours, 2 hours before, etc.)</li>
        <li>Choose delivery method:
          <ul>
            <li>Email reminders with calendar attachments</li>
            <li>SMS text reminders</li>
            <li>Automated phone calls</li>
            <li>Push notifications (mobile app)</li>
          </ul>
        </li>
        <li>Customize message templates</li>
        <li>Include confirmation links</li>
        <li>Track delivery and responses</li>
      </ol>
      
      <h3 id="rescheduling">Rescheduling and Cancellations</h3>
      <p>Handle changes efficiently:</p>
      
      <h4>Rescheduling</h4>
      <ol>
        <li>Open the appointment</li>
        <li>Click "Reschedule"</li>
        <li>Select new date/time</li>
        <li>Choose notification options:
          <ul>
            <li>Notify customer immediately</li>
            <li>Notify assigned technician</li>
            <li>Update any linked appointments</li>
          </ul>
        </li>
        <li>Add reason for change</li>
        <li>Confirm reschedule</li>
      </ol>
      
      <h4>Cancellations</h4>
      <ol>
        <li>Open the appointment</li>
        <li>Click "Cancel"</li>
        <li>Select cancellation reason:
          <ul>
            <li>Customer request</li>
            <li>Weather</li>
            <li>Technician unavailable</li>
            <li>Other</li>
          </ul>
        </li>
        <li>Choose follow-up action:
          <ul>
            <li>Offer to reschedule</li>
            <li>Add to callback list</li>
            <li>No further action</li>
          </ul>
        </li>
        <li>Send cancellation notice</li>
      </ol>
      
      <h3 id="route-optimization">Route Optimization</h3>
      <p>Maximize efficiency with smart routing:</p>
      <ul>
        <li><strong>Auto-routing</strong> - System suggests optimal order</li>
        <li><strong>Manual adjustment</strong> - Drag to reorder stops</li>
        <li><strong>Zone scheduling</strong> - Group appointments by area</li>
        <li><strong>Time windows</strong> - Respect customer preferences</li>
        <li><strong>Traffic consideration</strong> - Account for peak hours</li>
        <li><strong>Multi-stop optimization</strong> - Minimize total drive time</li>
      </ul>
      
      <h3 id="calendar-sync">Calendar Synchronization</h3>
      <p>Sync with external calendars:</p>
      <ol>
        <li>Go to Settings → Integrations</li>
        <li>Choose calendar service:
          <ul>
            <li>Google Calendar</li>
            <li>Outlook/Office 365</li>
            <li>Apple Calendar</li>
          </ul>
        </li>
        <li>Authorize connection</li>
        <li>Configure sync settings:
          <ul>
            <li>One-way or two-way sync</li>
            <li>Which calendars to sync</li>
            <li>Sync frequency</li>
            <li>Field mapping</li>
          </ul>
        </li>
        <li>Test synchronization</li>
      </ol>
      
      <h3 id="reporting">Appointment Reports</h3>
      <p>Track performance with built-in reports:</p>
      <ul>
        <li><strong>Daily Schedule</strong> - Printable daily runsheets</li>
        <li><strong>Completion Rate</strong> - Completed vs scheduled</li>
        <li><strong>No-show Analysis</strong> - Identify problem patterns</li>
        <li><strong>Technician Utilization</strong> - Hours booked vs available</li>
        <li><strong>Revenue by Appointment</strong> - Financial performance</li>
        <li><strong>Customer Frequency</strong> - Service interval analysis</li>
      </ul>
    `,
    tableOfContents: [
      { id: 'creating-appointments', title: 'Creating New Appointments' },
      { id: 'calendar-views', title: 'Calendar Views' },
      { id: 'appointment-details', title: 'Appointment Information' },
      { id: 'recurring-appointments', title: 'Recurring Appointments' },
      { id: 'appointment-status', title: 'Status Management' },
      { id: 'conflict-prevention', title: 'Preventing Double Booking' },
      { id: 'appointment-reminders', title: 'Automated Reminders' },
      { id: 'rescheduling', title: 'Rescheduling & Cancellations' },
      { id: 'route-optimization', title: 'Route Optimization' },
      { id: 'calendar-sync', title: 'Calendar Synchronization' },
      { id: 'reporting', title: 'Appointment Reports' }
    ],
    relatedArticles: ['calendar-sync-setup', 'route-optimization', 'appointment-reminders']
  },

  // ============ INVOICING & ESTIMATES ============
  {
    id: 'fin-1',
    title: 'Complete Invoicing and Billing Guide',
    slug: 'invoicing-billing-guide',
    description: 'Master the financial side of your business with comprehensive invoicing tools',
    category: 'finances',
    tags: ['invoices', 'billing', 'payments', 'accounting'],
    lastUpdated: new Date('2024-01-25'),
    readTime: 14,
    content: `
      <h2>Invoicing and Billing System</h2>
      <p>Professional invoicing is crucial for cash flow and customer satisfaction. This guide covers all aspects of creating, sending, and managing invoices.</p>
      
      <h3 id="creating-invoices">Creating Professional Invoices</h3>
      <p>Generate invoices quickly and accurately:</p>
      
      <h4>Step-by-Step Invoice Creation</h4>
      <ol>
        <li><strong>Navigate to Invoices</strong>
          <ul>
            <li>Go to Finances → Invoices</li>
            <li>Click "Create New Invoice"</li>
          </ul>
        </li>
        <li><strong>Select Customer</strong>
          <ul>
            <li>Search by name, phone, or address</li>
            <li>Customer details auto-populate</li>
            <li>Verify billing address</li>
          </ul>
        </li>
        <li><strong>Invoice Details</strong>
          <ul>
            <li>Invoice number (auto-generated or custom)</li>
            <li>Invoice date</li>
            <li>Due date (based on payment terms)</li>
            <li>PO number (if applicable)</li>
          </ul>
        </li>
        <li><strong>Add Line Items</strong>
          <ul>
            <li>Select from service catalog</li>
            <li>Or add custom items</li>
            <li>Specify quantity and rate</li>
            <li>Add item descriptions</li>
          </ul>
        </li>
        <li><strong>Apply Adjustments</strong>
          <ul>
            <li>Discounts (percentage or fixed)</li>
            <li>Taxes (automatic or manual)</li>
            <li>Deposits or credits</li>
          </ul>
        </li>
        <li><strong>Review and Save</strong></li>
      </ol>
      
      <h3 id="line-items">Managing Line Items</h3>
      <p>Build detailed invoices with flexible line items:</p>
      
      <h4>Service Items</h4>
      <ul>
        <li><strong>Pre-defined Services</strong> - Select from your service catalog</li>
        <li><strong>Custom Services</strong> - Add one-time services</li>
        <li><strong>Package Deals</strong> - Bundle multiple services</li>
        <li><strong>Recurring Services</strong> - Link to service agreements</li>
      </ul>
      
      <h4>Product Items</h4>
      <ul>
        <li>Physical products sold</li>
        <li>Inventory tracking integration</li>
        <li>Cost and markup calculations</li>
        <li>Serial numbers or batch tracking</li>
      </ul>
      
      <h4>Labor Charges</h4>
      <ul>
        <li>Hourly rates by technician level</li>
        <li>Overtime and premium rates</li>
        <li>Travel time charges</li>
        <li>Minimum service charges</li>
      </ul>
      
      <h3 id="pricing-discounts">Pricing and Discounts</h3>
      <p>Flexible pricing options:</p>
      <ul>
        <li><strong>Customer-specific pricing</strong> - Set special rates for VIP customers</li>
        <li><strong>Volume discounts</strong> - Automatic discounts based on quantity</li>
        <li><strong>Promotional codes</strong> - Apply marketing discounts</li>
        <li><strong>Early payment discounts</strong> - Incentivize quick payment</li>
        <li><strong>Package pricing</strong> - Bundled service discounts</li>
      </ul>
      
      <h3 id="tax-handling">Tax Configuration</h3>
      <p>Handle taxes correctly:</p>
      <ol>
        <li>Configure tax rates by location</li>
        <li>Set up tax exemptions for eligible customers</li>
        <li>Multiple tax types (state, local, special)</li>
        <li>Tax-inclusive or tax-exclusive pricing</li>
        <li>Automatic tax calculations</li>
        <li>Tax reporting features</li>
      </ol>
      
      <h3 id="sending-invoices">Sending Invoices to Customers</h3>
      <p>Multiple delivery options:</p>
      
      <h4>Email Delivery</h4>
      <ol>
        <li>Click "Send Invoice" button</li>
        <li>Review recipient email addresses</li>
        <li>Customize email message</li>
        <li>Attach additional documents if needed</li>
        <li>Send immediately or schedule for later</li>
        <li>Track email opens and views</li>
      </ol>
      
      <h4>Customer Portal</h4>
      <ul>
        <li>Customers can view invoices online</li>
        <li>Download PDF copies</li>
        <li>View payment history</li>
        <li>Make online payments</li>
        <li>Set up autopay</li>
      </ul>
      
      <h4>Print and Mail</h4>
      <ul>
        <li>Generate print-ready PDFs</li>
        <li>Bulk printing options</li>
        <li>Include return envelopes</li>
        <li>Track mailed invoices</li>
      </ul>
      
      <h3 id="payment-collection">Payment Collection</h3>
      <p>Streamline payment collection:</p>
      
      <h4>Online Payments (Stripe Integration)</h4>
      <ol>
        <li>Enable Stripe in Settings → Payment Methods</li>
        <li>Customers receive "Pay Now" button in emails</li>
        <li>Accept credit/debit cards</li>
        <li>ACH bank transfers</li>
        <li>Digital wallets (Apple Pay, Google Pay)</li>
        <li>Automatic payment recording</li>
      </ol>
      
      <h4>Manual Payment Recording</h4>
      <ol>
        <li>Open the invoice</li>
        <li>Click "Record Payment"</li>
        <li>Enter payment details:
          <ul>
            <li>Payment amount</li>
            <li>Payment date</li>
            <li>Payment method (cash, check, card)</li>
            <li>Reference number</li>
            <li>Notes</li>
          </ul>
        </li>
        <li>Apply to specific line items if partial</li>
        <li>Save payment record</li>
      </ol>
      
      <h3 id="invoice-status">Invoice Status Tracking</h3>
      <p>Monitor invoice lifecycle:</p>
      <ul>
        <li><strong>Draft</strong> - Not yet sent to customer</li>
        <li><strong>Sent</strong> - Delivered to customer</li>
        <li><strong>Viewed</strong> - Customer opened invoice</li>
        <li><strong>Partially Paid</strong> - Partial payment received</li>
        <li><strong>Paid</strong> - Fully paid</li>
        <li><strong>Overdue</strong> - Past due date</li>
        <li><strong>Void</strong> - Cancelled invoice</li>
        <li><strong>Bad Debt</strong> - Written off</li>
      </ul>
      
      <h3 id="payment-reminders">Automated Payment Reminders</h3>
      <p>Set up automatic follow-ups:</p>
      <ol>
        <li>Configure reminder schedule:
          <ul>
            <li>X days before due date</li>
            <li>On due date</li>
            <li>X days after due date</li>
            <li>Escalating reminders</li>
          </ul>
        </li>
        <li>Customize reminder messages</li>
        <li>Choose delivery method (email/SMS)</li>
        <li>Exclude specific customers</li>
        <li>Stop reminders when paid</li>
      </ol>
      
      <h3 id="recurring-invoices">Recurring Invoices</h3>
      <p>Automate billing for regular customers:</p>
      <ol>
        <li>Create template invoice</li>
        <li>Set recurrence pattern:
          <ul>
            <li>Weekly/Monthly/Quarterly/Annually</li>
            <li>Custom intervals</li>
            <li>Specific day of month</li>
          </ul>
        </li>
        <li>Configure auto-send options</li>
        <li>Link to service agreements</li>
        <li>Automatic price adjustments</li>
        <li>Pause/resume as needed</li>
      </ol>
      
      <h3 id="estimates">Creating and Managing Estimates</h3>
      <p>Win more business with professional estimates:</p>
      
      <h4>Estimate Creation</h4>
      <ol>
        <li>Go to Finances → Estimates</li>
        <li>Click "New Estimate"</li>
        <li>Similar process to invoices</li>
        <li>Add detailed scope of work</li>
        <li>Include terms and conditions</li>
        <li>Set expiration date</li>
        <li>Add optional items</li>
      </ol>
      
      <h4>Estimate Features</h4>
      <ul>
        <li>Multiple pricing options (Good/Better/Best)</li>
        <li>Photo attachments</li>
        <li>Digital signatures</li>
        <li>Online approval process</li>
        <li>Convert to invoice when approved</li>
        <li>Track win/loss rates</li>
      </ul>
      
      <h3 id="financial-reports">Financial Reports</h3>
      <p>Track business performance:</p>
      <ul>
        <li><strong>Accounts Receivable Aging</strong> - Outstanding invoice analysis</li>
        <li><strong>Revenue Reports</strong> - Income by period/service/customer</li>
        <li><strong>Payment Reports</strong> - Collections and methods</li>
        <li><strong>Tax Reports</strong> - Tax collected and owed</li>
        <li><strong>Customer Statements</strong> - Account history for customers</li>
        <li><strong>Profit Margins</strong> - Service profitability analysis</li>
      </ul>
      
      <div class="bg-green-50 p-4 rounded-lg">
        <strong>Best Practices:</strong>
        <ul>
          <li>Send invoices immediately after service completion</li>
          <li>Offer multiple payment options</li>
          <li>Set clear payment terms</li>
          <li>Follow up on overdue invoices promptly</li>
          <li>Keep detailed records for tax purposes</li>
          <li>Regular reconciliation with bank accounts</li>
        </ul>
      </div>
    `,
    tableOfContents: [
      { id: 'creating-invoices', title: 'Creating Invoices' },
      { id: 'line-items', title: 'Managing Line Items' },
      { id: 'pricing-discounts', title: 'Pricing and Discounts' },
      { id: 'tax-handling', title: 'Tax Configuration' },
      { id: 'sending-invoices', title: 'Sending Invoices' },
      { id: 'payment-collection', title: 'Payment Collection' },
      { id: 'invoice-status', title: 'Invoice Status' },
      { id: 'payment-reminders', title: 'Payment Reminders' },
      { id: 'recurring-invoices', title: 'Recurring Invoices' },
      { id: 'estimates', title: 'Estimates' },
      { id: 'financial-reports', title: 'Financial Reports' }
    ],
    relatedArticles: ['payment-processing', 'estimates-guide', 'financial-reports']
  }
];